import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import { OVERLAYS_URL } from '../../../Settings';
import {
    clockOutableStaff,
} from '../../Game/GamePlayerInfo';
import blank512
    from '../../TwoD/Common/blank-512x512.png';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';

import '../../TwoD/Components/Controls/Actions/action-scroller.css';
import '../../TwoD/Choices/lodging-action.css';
import './ActionClockout.css';

const STAFF_TEXTURE_ID = 157;
const LODGING_TEXTURE_ID = 72;

const OVERLAY_JUICE = OVERLAYS_URL
    + 'LodgingJuiceOverlay-1080x210.png';
const OVERLAY_DEFEND = OVERLAYS_URL
    + 'AntiJuiceOverlay-1080x210.png';

function defaultOverlay(mode) {
    return (mode === 'defend') ? OVERLAY_DEFEND : OVERLAY_JUICE;
}

// ActionClockout — verbatim port of the juice-bar Swipers from
// LodgingActionJuice (mode='juice') and AntiJuice2d
// (mode='defend'). Same per-row anchor + lodging-action-background
// overlay <img>, same in-Swiper Reset/Skip/Done SwiperSlides, same
// position-arithmetic onClick on swiper.clickedIndex, same
// purchased-action-container spinning-robot overlay on slots where
// `index + 1 > startingFate`.
//
// startingFate is captured from gamePlayerData.current_fate on the
// component's first render and held for the life of the mount —
// matches the source's componentDidMount-captured semantics.
// LodgingActionWindow remounts ActionClockout on every juice <-> add
// <-> defend transition, so re-capture happens naturally.
//
// soldRobots is owned by LodgingActionWindow and threaded down.
// onClockOut(robot), onReset(), onCommit(soldRobots) are the
// callbacks the window wires up to the right gamePlayerApi method.
export function ActionClockout({
    gamePlayerData, mode, soldRobots, isSelfTarget = true,
    actionTextureId = LODGING_TEXTURE_ID,
    overlay,
    onClockOut, onReset, onCommit,
}) {
    const sold = Array.isArray(soldRobots) ? soldRobots : [];

    // startingFate captured once at mount. useState's initialiser
    // function only runs on first render — same semantics as the
    // source's componentDidMount setState.
    const [startingFate] = useState(() => (gamePlayerData
        && typeof gamePlayerData.current_fate === 'number')
        ? gamePlayerData.current_fate : 0);

    // sellableRobots: clockoutable staff still in storage. The
    // source uses componentDidUpdate to reset when the backend's
    // clockoutable count grows; we observe gamePlayerData via the
    // useEffect below to mirror that.
    const initialClockoutable = useRef(null);
    if (initialClockoutable.current === null && gamePlayerData) {
        try {
            initialClockoutable.current = clockOutableStaff(gamePlayerData);
        } catch (_err) {
            initialClockoutable.current = [];
        }
    }
    const [sellableRobots, setSellableRobots] = useState(
        () => initialClockoutable.current || []);

    // Source componentDidUpdate: if the player's clockoutable list
    // grows above current local tracking, reset. Same shape here.
    useEffect(() => {
        if (!gamePlayerData) return;
        let all = [];
        try { all = clockOutableStaff(gamePlayerData); }
        catch (_err) { all = []; }
        if (all.length > sellableRobots.length + sold.length) {
            setSellableRobots(all);
        }
    }, [gamePlayerData, sellableRobots.length, sold.length]);

    // After a clockout the parent's soldRobots has one more entry.
    // The source pops sellableRobots on each clockout; mirror that
    // by trimming sellableRobots whenever soldRobots grows past the
    // remaining sellable count tracked here.
    useEffect(() => {
        if (sold.length > 0
            && initialClockoutable.current
            && sellableRobots.length
                > initialClockoutable.current.length - sold.length) {
            setSellableRobots(
                initialClockoutable.current.slice(
                    0, initialClockoutable.current.length - sold.length));
        }
        if (sold.length === 0
            && initialClockoutable.current
            && sellableRobots.length
                < initialClockoutable.current.length) {
            // Reset path: parent cleared soldRobots, restore the
            // full initial list.
            setSellableRobots(initialClockoutable.current);
        }
    }, [sold.length, sellableRobots.length]);

    const currentFate = (mode === 'defend')
        ? Math.max(0, startingFate - sold.length * 2)
        : startingFate + sold.length * 2;

    const textureId = actionTextureId;
    const texture = getTextureUrl(textureId);
    const staffTexture = getTextureUrl(STAFF_TEXTURE_ID);

    const getSwiperData = () => {
        const selectors = Array(currentFate).fill(textureId);
        const robots = Array(sellableRobots.length).fill(STAFF_TEXTURE_ID);
        return [...selectors, ...robots];
    };

    // Verbatim port of the source actionImage. For staff slots
    // (texture===157) render the staff icon and bail. For action
    // slots, render the action texture and overlay the spinning
    // purchased-action-container when `index + 1 > startingFate`.
    const renderActionImage = (slotTexture, index) => {
        if (slotTexture === STAFF_TEXTURE_ID) {
            return (
                <div data-testid={'action-clockout-staff'}>
                    <img src={staffTexture}
                         alt={'action'}/>
                </div>
            );
        }
        const isPurchased = (index + 1 > startingFate);
        return (
            <div data-testid={'action-clockout-fate'}>
                <img src={texture}
                     alt={'action'}/>
                {isPurchased && (
                    <div className={
                            'action-clockout-purchased-container'}
                         data-testid={
                             'action-clockout-purchased'}>
                        <div className={
                                'action-clockout-purchased-parent'}>
                            <div className={
                                    'action-clockout-purchased-'
                                    + 'robot-container'}>
                                <img src={staffTexture}
                                     alt={'robot'}
                                     className={
                                         'action-clockout-'
                                         + 'purchased-robot'}/>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const handleStaffClick = () => {
        if (!sellableRobots.length) return;
        const next = [...sellableRobots];
        const robot = next.pop();
        setSellableRobots(next);
        if (typeof onClockOut === 'function') onClockOut(robot);
    };

    const handleReset = () => {
        if (initialClockoutable.current) {
            setSellableRobots(initialClockoutable.current);
        }
        if (typeof onReset === 'function') onReset();
    };

    const handleCommit = () => {
        if (typeof onCommit === 'function') onCommit(sold);
    };

    // Port of LodgingActionJuice.onJuiceBarClick with Confirm button
    // for opp-target. Self-target keeps the legacy behavior (pool /
    // upgrade click commits via lodgingFullAction); opp-target gets an
    // explicit Confirm here that fires onCommit → lodgingJuice.
    const onJuiceBarClick = (swiper) => {
        let index = swiper.clickedIndex;
        const hasReset = sold.length > 0;
        const hasSkip = currentFate === 0;
        const hasConfirm = !isSelfTarget && currentFate > 0;

        let prefixIndex = 0;
        if (hasReset) {
            if (index === prefixIndex) return handleReset();
            prefixIndex++;
        }
        if (hasSkip) {
            if (index === prefixIndex) {
                handleCommit();
                return;
            }
            prefixIndex++;
        }
        if (hasConfirm) {
            if (index === prefixIndex) {
                handleCommit();
                return;
            }
            prefixIndex++;
        }

        const dataIndex = index - prefixIndex;
        if (dataIndex < 0) return;
        const item = getSwiperData()[dataIndex];
        if (item === STAFF_TEXTURE_ID) {
            handleStaffClick();
        }
        // LODGING_TEXTURE_ID — no-op, dead historical branch.
    };

    // Derived from AntiJuice2d.onSwiperClick, generalized to the same
    // prefix-counting scheme onJuiceBarClick uses so the ALWAYS-present
    // defend commit button (see the button block below) doesn't desync
    // the slot math. The old form only offered a commit slide when
    // currentFate === 0, so a defender who did not want to clock out
    // any staff had no way to commit the legal empty defense
    // (clickAntiJuice([]), the engine's "we can not resist" path) — the
    // window pushed the cart but stranded a no-clockout defender (G147
    // follow-up). Order of prefix slides here mirrors the JSX exactly:
    // Reset (if staff sold), then Done/Skip, then the glyph data.
    const onAntiJuiceClick = (swiper) => {
        const swiperData = getSwiperData();
        let index = swiper.clickedIndex;
        const hasReset = sold.length > 0;
        let prefixIndex = 0;
        if (hasReset) {
            if (index === prefixIndex) return handleReset();
            prefixIndex++;
        }
        // Defend always offers commit — with zero staff clocked out it
        // is the legal decline (engine anti_juice accepts sold_robots=[]).
        if (index === prefixIndex) return handleCommit();
        prefixIndex++;
        const dataIndex = index - prefixIndex;
        if (dataIndex < 0) return;
        const item = swiperData[dataIndex];
        if (!item) return;
        if (item === STAFF_TEXTURE_ID) {
            handleStaffClick();
        } else {
            handleCommit();
        }
    };

    const onClick = (mode === 'defend')
        ? onAntiJuiceClick
        : onJuiceBarClick;

    const overlaySrc = overlay || defaultOverlay(mode);

    // Defend can commit with zero clockout (the legal empty defense).
    // Read 'Skip' until staff are clocked out — matching the juice
    // bar's Skip affordance the player already knows — then 'Done'.
    const commitLabel = (mode === 'defend')
        ? (sold.length > 0 ? 'Done' : 'Skip')
        : 'Skip';

    return (
        <div className={'action-clockout-anchor '
                + 'action-clockout--' + mode}
             id={'juicer'}
             data-testid={'action-clockout'}
             data-mode={mode}>
            <img src={overlaySrc}
                 alt={'action'}
                 data-testid={'action-clockout-overlay'}
                 className={'lodging-action-background'}/>
            <div className={'action-clockout-flex'}>
                <div className={'action-clockout-flex-item'}>
                    <Swiper slidesPerView={6}
                            spaceBetween={0}
                            freeMode={true}
                            grabCursor={true}
                            modules={[Virtual]}
                            onClick={onClick}
                            className={'action-scroller-swiper'}>
                        {sold.length > 0 && (
                            <div className={
                                    'action-clockout-flex-button'}>
                                <SwiperSlide key={'gi--1'}
                                             virtualIndex={-1}>
                                    <Button variant={'stem'}
                                            data-testid={
                                                'action-clockout-'
                                                + 'reset'}>
                                        Reset
                                    </Button>
                                </SwiperSlide>
                            </div>
                        )}
                        {(currentFate === 0 || mode === 'defend') && (
                            <div className={
                                    'action-clockout-flex-button'}>
                                <SwiperSlide
                                    key={'gi--2'}
                                    virtualIndex={-2}>
                                    <Button variant={'stem'}
                                            data-testid={
                                                'action-clockout-'
                                                + 'commit'}>
                                        {commitLabel}
                                    </Button>
                                </SwiperSlide>
                            </div>
                        )}
                        {mode === 'juice' && !isSelfTarget
                            && currentFate > 0 && (
                            <div className={
                                    'action-clockout-flex-button'}>
                                <SwiperSlide
                                    key={'gi--3'}
                                    virtualIndex={-3}>
                                    <Button variant={'stem'}
                                            data-testid={
                                                'action-clockout-'
                                                + 'confirm'}>
                                        Confirm
                                    </Button>
                                </SwiperSlide>
                            </div>
                        )}
                        {getSwiperData().map((selector, index) => (
                            <SwiperSlide key={'gi-' + index}
                                         virtualIndex={index}>
                                {renderActionImage(selector, index)}
                            </SwiperSlide>
                        ))}
                        {mode === 'juice' && currentFate === 0 && (
                            <div className={
                                    'action-clockout-flex-button'}>
                                <SwiperSlide key={'gi-999'}
                                             virtualIndex={999}>
                                    <img src={blank512}
                                         alt={'blank'}
                                         className={
                                             'action-clockout-'
                                             + 'blank-image'}/>
                                </SwiperSlide>
                            </div>
                        )}
                    </Swiper>
                </div>
            </div>
        </div>
    );
}

export default ActionClockout;
